/* eslint-disable */
import React from "react";

/**
 * Shared FX snapshot chip for both the admin reports header and the
 * public `/redeem` quote panel.
 *
 * Renders a small pill summarising the live USD→EUR snapshot the
 * backend already attached to the response payload:
 *
 *   "FX · eur_per_usd=0.85027000 · frankfurter_ecb · FRESH · 19m ago"
 *
 * Visual state:
 *   - Green/fresh  when `snap.stale === false`
 *   - Amber/stale  when `snap.stale === true`
 *   - "legacy" muted placeholder when `snap` is null / undefined
 *     (legacy quote response, or pre-Step-3 deploys).
 *
 * Strict rules:
 *   * Display-only. No network call. Reads `snap` directly from a
 *     response payload the caller already has in hand.
 *   * Never renders an API key (the backend never puts one in `snap`).
 */
export default function FxChip({ snap, compact = false, testid = "fx-chip" }) {
  if (!snap) {
    return (
      <span
        data-testid={testid}
        data-state="legacy"
        className="inline-flex items-center gap-1 rounded border border-white/15 bg-white/5 px-2 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-white/45"
      >
        FX · legacy
      </span>
    );
  }
  const stale = !!snap.stale;
  const ago = (() => {
    const a = Number(snap.age_seconds);
    if (!Number.isFinite(a)) return "—";
    if (a < 60) return `${a}s ago`;
    if (a < 3600) return `${Math.floor(a / 60)}m ago`;
    if (a < 86400) return `${Math.floor(a / 3600)}h ago`;
    return `${Math.floor(a / 86400)}d ago`;
  })();
  const palette = stale
    ? "border-[#FFB800]/40 bg-[#FFB800]/10 text-[#FFB800]"
    : "border-[#39FF14]/40 bg-[#39FF14]/5 text-[#39FF14]";
  return (
    <span
      data-testid={testid}
      data-state={stale ? "stale" : "fresh"}
      title={snap.fx_source || ""}
      className={`inline-flex items-center gap-1 rounded border px-2 py-1 font-mono text-[10px] uppercase tracking-[0.15em] ${palette}`}
    >
      <span aria-hidden="true" className={`h-1.5 w-1.5 rounded-full ${stale ? "bg-[#FFB800]" : "bg-[#39FF14]"}`} />
      {compact ? (
        <>FX · {snap.eur_per_usd ?? "—"} · {stale ? "STALE" : "fresh"} · {ago}</>
      ) : (
        <>FX · eur_per_usd={snap.eur_per_usd ?? "—"} · {snap.fx_label || "—"} · {stale ? "STALE" : "fresh"} · {ago}</>
      )}
    </span>
  );
}
